import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvent, AnalyticsBloc, AnalyticsPageName } from '@kanalytics';
import { EnvironmentVariablesService } from '@kenv';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { DataLinkBloc } from '@kp/data-link/data-link.bloc';
import { DialogueLogicKey } from '@kp/dialogue/models/dialogue.model';
import { EligibilityCandidate } from '@kp/eligibility/eligibility-candidate.model';
import { LegalDocumentsBloc } from '@kp/legal-document/legal-documents.bloc';
import { OnboardingBloc } from '@kp/onboarding/onboarding.bloc';
import { DataStoreService, TitleService } from '@kservice';
import { UserEvent, UserType } from '@ktypes/enums';
import { LegalDocumentType, Status } from '@ktypes/models';
import { Subscription } from 'rxjs';

@Component({
  selector: 'kp-screen-privacy-pledge',
  templateUrl: './screen-privacy-pledge.component.html',
  styleUrls: ['./screen-privacy-pledge.component.scss'],
})
export class ScreenPrivacyPledgeComponent implements OnInit, OnDestroy {
  private _title = 'Privacy Pledge';
  legalDocumentType = LegalDocumentType;
  candidate: EligibilityCandidate = null;
  requireAcceptance = false;
  policyType;
  showFullPolicy = false;
  fullPolicyAccepted = false;
  acceptInProgress = false;
  statusChecked = false;
  subscriptions: Subscription[] = [];
  summary = '';

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _analyticsBloc: AnalyticsBloc,
    private _authenticationBloc: AuthenticationBloc,
    private _dataStoreService: DataStoreService,
    private _environmentVariablesService: EnvironmentVariablesService,
    public legalDocumentsBloc: LegalDocumentsBloc,
    private _onboardingBloc: OnboardingBloc,
    private _router: Router,
    private _titleService: TitleService
  ) {}

  ngOnInit() {
    this._titleService.set(this._title);
    void this.legalDocumentsBloc.fetchLegalDocuments(null);
    this.candidate = this._onboardingBloc.getEligibilityCandidateData();
    this.sendUserEvent(UserEvent.onboarding_user_view_privacy_policy);

    if (this._onboardingBloc.isOnboarding) {
      this._onboardingBloc.setLatestPage('/welcome');
    }

    this.subscriptions.push(
      this.legalDocumentsBloc.currentLegalDocumentStatus$.subscribe((currentLegalDocumentStatus) => {
        if ([Status.done, Status.error].includes(currentLegalDocumentStatus?.status)) {
          this.statusChecked = true;
          const legalDocsNeedSigning = currentLegalDocumentStatus?.data?.some((docStatus) => !docStatus.signed);
          // only used for pulse survey currently; only show if docs aren't signed
          this.requireAcceptance =
            legalDocsNeedSigning &&
            (DataLinkBloc.isExternalPulseSurvey || this._dataStoreService.authData.user.type === UserType.pulse);

          if (this.requireAcceptance) {
            this.legalDocumentsBloc.resetServerSideLegalDocumentsAcceptance();
          }
        }
      }),
      this.legalDocumentsBloc.serverSideAcceptLegalDocumentsResult$.subscribe((legalDocAcceptanceRecorded) => {
        if (this.requireAcceptance) {
          if (legalDocAcceptanceRecorded === null) {
            return;
          }
          if (legalDocAcceptanceRecorded) {
            this._navigateToPulseSurvey();
          } else {
            void this._router.navigate(['/error']);
          }
          this.acceptInProgress = false;
        }
      })
    );

    if (this.legalDocumentsBloc.currentLegalDocumentStatus == null) {
      this.legalDocumentsBloc.getCurrentLegalDocumentStatus();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription && subscription.unsubscribe());
  }

  continue() {
    this.sendUserEvent(UserEvent.onboarding_user_agreed_privacy_policy);

    if (this.requireAcceptance) {
      if (this.acceptInProgress) {
        return;
      }

      this._analyticsBloc.sendEvent(
        this._analyticsBloc.createEventFromInteraction({
          page: AnalyticsPageName.privacyPledge,
          event: AnalyticEvent.privacy_pledge_accepted,
        })
      );
      if (this.fullPolicyAccepted) {
        this.acceptInProgress = true;
        this.legalDocumentsBloc.serverSideAcceptLegalDocuments();
      }
    } else if (DataLinkBloc.isExternalPulseSurvey) {
      this._navigateToPulseSurvey();
    } else {
      void this._router.navigate(['/dialogue/onboarding'], {
        queryParams: {
          logicKey: DialogueLogicKey[`dialogue_${this._environmentVariablesService.product}_onboarding_v1`],
          returnUrl: '',
        },
      });
    }
  }

  private _navigateToPulseSurvey() {
    this._authenticationBloc.checkForExistingAccount(this.candidate?.candidateId);
    // once done, clear out legal docs to prevent redirect issues
    this.legalDocumentsBloc.clearLegalDocumentStatus(true);
    this.legalDocumentsBloc.clearLegalDocumentStorage();
    void this._router.navigate(['/pulse_survey']);
  }

  sendUserEvent(eventKey: UserEvent) {
    if (this.candidate && this.candidate.candidateId) {
      this._onboardingBloc.sendOnboardingUserEvent(eventKey);
    }
  }

  privacyCheckboxChanged(checkboxChecked) {
    this.fullPolicyAccepted = checkboxChecked;
  }

  isFullPolicyOpen(isOpen: boolean) {
    this.showFullPolicy = isOpen;
    if (!isOpen) {
      delete this.policyType;
    }
  }

  clickFullPolicyLink($event: UIEvent, documentType = LegalDocumentType.PRIVACY) {
    this.policyType = documentType;
    this.showFullPolicy = true;

    $event.stopPropagation();
    $event.preventDefault();
    return false;
  }
}
